/**
 * RBAC - Role-Based Access Control Utility
 * Maps admin pages to required permissions
 */

// Page to permissions mapping
export const PAGE_PERMISSIONS = {
  '/admin': ['*'], // Dashboard - available to all admin roles
  '/admin/orders': ['orders.view'],
  '/admin/clients': ['clients.view'],
  '/admin/products': ['products.view'],
  '/admin/payments': ['payments.view'],
  '/admin/support': ['support.view'],
  '/admin/delivery': ['delivery.view'],
  '/admin/analytics': ['analytics.view'],
  '/admin/production': ['orders.view'], // Production needs orders access
  '/admin/settings': ['settings.view', 'settings.edit'],
  '/admin/integrations': ['integrations.view', 'integrations.manage'],
  '/admin/security': ['users.view', 'users.edit'],
  '/admin/notifications': ['settings.view'],
  '/admin/cities': ['settings.view', 'settings.edit'],
  '/admin/discount-groups': ['clients.view', 'clients.edit'],
  '/admin/content': ['content.view'],
  '/admin/content-creators': ['users.view'],
  '/production': ['orders.view'],
  '/analytics': ['analytics.view'],
  '/media-studio': ['content.view', 'content.create'],
};

// Navigation items with permissions
export const ADMIN_NAV_ITEMS = [
  { path: '/admin/orders', label: 'Заказы', permission: 'orders.view', icon: 'ShoppingCart' },
  { path: '/admin/products', label: 'Товары', permission: 'products.view', icon: 'Package' },
  { path: '/admin/payments', label: 'Платежи', permission: 'payments.view', icon: 'CreditCard' },
  { path: '/admin/clients', label: 'Клиенты', permission: 'clients.view', icon: 'Users' },
  { path: '/admin/support', label: 'Поддержка', permission: 'support.view', icon: 'MessageSquare' },
  { path: '/admin/delivery', label: 'Доставка', permission: 'delivery.view', icon: 'Truck' },
  { path: '/admin/cities', label: 'Города', permission: 'settings.view', icon: 'MapPin' },
  { path: '/analytics', label: 'Аналитика', permission: 'analytics.view', icon: 'BarChart3' },
  { path: '/production', label: 'Производство', permission: 'orders.view', icon: 'Factory' },
  { path: '/admin/content', label: 'Контент', permission: 'content.view', icon: 'FileText' },
  { path: '/admin/settings', label: 'Управление', permission: 'settings.view', icon: 'Settings' },
  { path: '/admin/integrations', label: 'Интеграции', permission: 'integrations.view', icon: 'Plug' },
  { path: '/admin/security', label: 'Безопасность', permission: 'users.view', icon: 'Shield' },
  { path: '/admin/notifications', label: 'Уведомления', permission: 'settings.view', icon: 'Bell' },
];

/**
 * Check if user has required permission
 * @param {string[]} userPermissions - Array of user permissions
 * @param {string|string[]} requiredPermissions - Required permission(s)
 * @returns {boolean}
 */
export function hasPermission(userPermissions, requiredPermissions) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Admin with "*" has all permissions
  if (userPermissions.includes('*')) return true;
  
  // If requiredPermissions is a string, convert to array
  const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  
  // Check if user has at least one of the required permissions
  return required.some(perm => userPermissions.includes(perm));
}

/**
 * Check if user can access a specific page
 * @param {string[]} userPermissions - Array of user permissions
 * @param {string} pagePath - Page path to check (e.g., '/admin/orders')
 * @returns {boolean}
 */
export function canAccessPage(userPermissions, pagePath) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Admin with "*" has all access
  if (userPermissions.includes('*')) return true;
  
  // Remove language prefix and get the base path
  const basePath = pagePath.replace(/^\/[a-z]{2}/, '');
  
  // Get required permissions for this page
  const requiredPermissions = PAGE_PERMISSIONS[basePath];
  
  // If page is not in the map, deny by default (except dashboard)
  if (!requiredPermissions) {
    // Allow access to base admin dashboard for any admin user
    if (basePath === '/admin' || basePath === '') return true;
    return false;
  }
  
  return hasPermission(userPermissions, requiredPermissions);
}

/**
 * Get navigation items filtered by user permissions
 * @param {string[]} userPermissions - Array of user permissions
 * @returns {Array} Filtered navigation items
 */
export function getFilteredNavItems(userPermissions) {
  if (!userPermissions || !Array.isArray(userPermissions)) return [];
  
  // Admin with "*" sees everything
  if (userPermissions.includes('*')) return ADMIN_NAV_ITEMS;
  
  return ADMIN_NAV_ITEMS.filter(item => 
    userPermissions.includes(item.permission)
  );
}

/**
 * Check if user is admin (has admin role or admin-level permissions)
 * @param {object} user - User object
 * @returns {boolean}
 */
export function isAdminUser(user) {
  if (!user) return false;
  
  // Check role
  const adminRoles = ['Admin', 'Manager', 'Support', 'Courier', 'ContentCreator'];
  if (adminRoles.includes(user.role)) return true;
  
  // Check if has any admin permissions (including custom roles)
  if (user.permissions && Array.isArray(user.permissions)) {
    if (user.permissions.includes('*')) return true;
    // Any permission means the user has an admin-level role
    if (user.permissions.length > 0) return true;
  }
  
  return false;
}
